import { Box, Heading, VStack, HStack, Text, SimpleGrid, Button, Badge, Icon, useColorModeValue, useBreakpointValue, Divider, Flex} from '@chakra-ui/react';
import { FaCheckCircle } from "react-icons/fa";
import PublicLayout from './PublicLayout';
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useNavigate } from "react-router-dom";
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import RegisterInterestComponent from './Components/RegisterInterestComponent';
import B2CMarkdownLongText from '../B2C-TestSeries/Components/B2CMarkDownLongText';

import bandpic from "../resources/EDUVIC Landing page1-coaching.png";
import { coachingCategories as categories } from './coachingCategories'

const markdownComponents = {
  h1: ({ children }: any) => (
    <Heading
      as="h1"
      size="sm"
      mb={2}
      lineHeight="1.3"
    >
      {children}
    </Heading>
  ),

  h2: ({ children }: any) => (
    <Heading
      as="h2"
      size="sm"
      fontWeight="semibold"
      mb={2}
      mt={3}
      lineHeight="1.3"
    >
      {children}
    </Heading>
  ),

  h3: ({ children }: any) => (
    <Heading
      as="h3"
      size="xs"
      fontWeight="semibold"
      mb={2}
      mt={2}
      lineHeight="1.3"
    >
      {children}
    </Heading>
  ),

  p: ({ children }: any) => (
    <Text
      mb={2}
      lineHeight="1.5"
    >
      {children}
    </Text>
  ),

  ul: ({ children }: any) => (
    <Box
      as="ul"
      pl={5}
      mb={2}
    >
      {children}
    </Box>
  ),

  ol: ({ children }: any) => (
    <Box
      as="ol"
      pl={5}
      mb={2}
    >
      {children}
    </Box>
  ),

  li: ({ children }: any) => (
    <Box
      as="li"
      mb={1}
      lineHeight="1.45"
    >
      {children}
    </Box>
  ),

  strong: ({ children }: any) => (
    <Text
      as="span"
      fontWeight="bold"
    >
      {children}
    </Text>
  ),

  em: ({ children }: any) => (
    <Text
      as="span"
      fontStyle="italic"
    >
      {children}
    </Text>
  ),

  // =========================
  // TABLE SUPPORT
  // =========================

  table: ({ children }: any) => (
    <Box
      width="100%"
      overflowX="auto"
      mb={3}
      borderWidth="1px"
      borderColor="gray.200"
      borderRadius="md"
    >
      <Box
        as="table"
        width="100%"
        sx={{ borderCollapse: "collapse" }}
        fontSize="sm"
      >
        {children}
      </Box>
    </Box>
  ),

  thead: ({ children }: any) => (
    <Box
      as="thead"
      bg="gray.50"
    >
      {children}
    </Box>
  ),

  tbody: ({ children }: any) => (
    <Box as="tbody">
      {children}
    </Box>
  ),

  tr: ({ children }: any) => (
    <Box
      as="tr"
      borderBottomWidth="1px"
      borderColor="gray.200"
      _last={{
        borderBottomWidth: 0,
      }}
    >
      {children}
    </Box>
  ),

  th: ({ children }: any) => (
    <Box
      as="th"
      textAlign="left"
      fontWeight="semibold"
      color="gray.700"
      px={3}
      py={2}
      borderRightWidth="1px"
      borderColor="gray.200"
      _last={{
        borderRightWidth: 0,
      }}
    >
      {children}
    </Box>
  ),

  td: ({ children }: any) => (
    <Box
      as="td"
      px={3}
      py={2}
      color="gray.700"
      borderRightWidth="1px"
      borderColor="gray.200"
      verticalAlign="top"
      _last={{
        borderRightWidth: 0,
      }}
    >
      {children}
    </Box>
  ),
};

const ServicesPage = () => {
  const navigate = useNavigate();
  const theme = useColorModeValue("light", "dark");

  useEffect(() => {
    document.title = "Eduvic Coaching — Guided Preparation for Selective Entry & Competitive Exams";

    const description =
      "Structured online coaching for Selective Entry and other competitive school entrance exams — scheduled classes, guided preparation and assessment, backed by the Eduvic Learning Platform.";
    let meta = document.querySelector(
      "meta[name='description']"
    ) as HTMLMetaElement | null;

    if (!meta) {
      meta = document.createElement("meta");
      meta.name = "description";
      document.head.appendChild(meta);
    }

    meta.content = description;
  }, []);
  
  const isMobile = useBreakpointValue({ base: true, md: false });
  const cardBg = useColorModeValue("white", "gray.800");
  const mutedText = useColorModeValue("gray.600", "gray.400");

  // ================= THEME / COLOUR CONSTANTS =================


const sectionBg = useColorModeValue("gray.50", "gray.900");

const bodyText = useColorModeValue("gray.700", "gray.200");

const platformBg = useColorModeValue("blue.50", "blue.900");
const platformCardBg = useColorModeValue("white", "gray.800");

const lightBorder = useColorModeValue("gray.200", "gray.700");
const subtleBorder = useColorModeValue("gray.100", "gray.700");

const stepNumberColor = useColorModeValue("blue.500", "blue.300");
const accentText = useColorModeValue("blue.600", "blue.300");

const sectionHeadingColor = useColorModeValue("gray.800", "white");
const secondaryHeadingColor = useColorModeValue("gray.200", "gray.700");

const heroOverlay = useColorModeValue(
  "rgba(0,0,0,0.68)",
  "rgba(0,0,0,0.72)"
);
  const { hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const element = document.querySelector(hash);
      if (element) {
        const yOffset = -80;
        const yPosition = element.getBoundingClientRect().top + window.scrollY + yOffset;
        window.scrollTo({ top: yPosition, behavior: 'smooth' });
      }
    }
  }, [hash]);

  const scrollTo = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const yOffset = -80;
      const yPosition = element.getBoundingClientRect().top + window.scrollY + yOffset;
      window.scrollTo({ top: yPosition, behavior: "smooth" });
    }
  };

  return (
    <PublicLayout>

      {/* ================= HERO ================= */}
      <Box as="section" height={{ base: "72vh", md: "68vh" }} bgImage={bandpic} bgSize="cover" bgPos="center" position="relative">
        <Box position="absolute" top="0" left="0" width="100%" height="100%"
          bgGradient="linear(to-r, rgba(0,0,0,0.65), rgba(0,0,0,0.25), rgba(0,0,0,0))" zIndex="1" />
        <Box position="relative" zIndex="2" height="100%" display="flex" alignItems="center" px={{ base: 6, md: 16 }}>
          <VStack align="flex-start" spacing={6} maxW="700px">
            <Heading color="white" size="2xl" lineHeight="1.2">
              Guided Preparation for Competitive School Entry
            </Heading>
            <Text color="gray.100" fontSize="lg" lineHeight="1.6">
              Practice builds familiarity. Guided preparation helps students
              understand, apply and improve.
            </Text>
            <Box display="flex" gap={4} flexWrap="wrap">
              <Box as="button" bg="blue.600" color="white" px={8} py={3} rounded="md" fontWeight="bold"
                boxShadow="md" _hover={{ bg: "blue.700", transform: "translateY(-2px)" }} transition="all 0.2s"
                onClick={() => scrollTo("preparation-areas")}>
                Explore Guided Preparation
              </Box>
              <Box as="button" bg="purple.600" color="white" px={8} py={3} rounded="md" fontWeight="bold"
                _hover={{ bg: "purple.700", transform: "translateY(-2px)" }} transition="all 0.2s"
                onClick={() => navigate("/practice")}>
                Explore Eduvic Practice Platform
              </Box>
            </Box>
          </VStack>
        </Box>
{/*         <Box position="absolute" top="2%" right="1%" zIndex="1000" maxW="sm" display={isMobile ? "none" : "block"}>
          <RegisterInterestComponent />
        </Box> */}
      </Box>


    {/* ================= WHAT IS EDUVIC COACHING ================= */}

    <Box
      maxW="1600px"
      mx="auto"
      px={{ base: 6, md: 8 }}
      py={{ base: 10, md: 14 }}
    >

      {/* SECTION INTRO */}

      <VStack
        spacing={4}
        mb={8}
        textAlign="center"
      >

{/*         <Text
          fontSize="sm"
          fontWeight="bold"
          color="blue.500"
          textTransform="uppercase"
          letterSpacing="wide"
        >
          EDUVIC COACHING
        </Text> */}

        <Heading
          size={{ base: "lg", md: "xl" }}
          color={sectionHeadingColor}
          lineHeight="1.25"
        >
          More Than Practice. Guided Preparation.
        </Heading>

        <Text
          color={mutedText}
          fontSize={{ base: "md", md: "lg" }}
          lineHeight="1.8"
          maxW="850px"
        >
          Eduvic Coaching is designed for students who need more than
          independent practice. Scheduled online classes provide structured
          guidance around concepts, problem solving, examination strategy and
          ongoing assessment, supported by the Eduvic Learning Platform and
          relevant Eduvic Practice resources.
        </Text>

      </VStack>


      {/* GUIDED PREPARATION PILLARS */}

      <Box
        bg={cardBg}
        border="1px solid"
        borderColor={lightBorder}
        borderRadius="2xl"
        shadow="md"
        p={{ base: 6, md: 10 }}
      >

        <SimpleGrid
          columns={{ base: 1, md: 3 }}
          spacing={5}
        >

          <Box
            bg={platformBg}
            borderRadius="xl"
            p={5}
          >
            <Text fontSize="2xl" mb={2}>
              🧠
            </Text>

            <Heading size="sm" mb={2}>
              Understand
            </Heading>

            <Text
              fontSize={{ base: "md", md: "md" }}
              color={mutedText}
              lineHeight="1.7"
            >
              Build the underlying concepts first and address foundation gaps
              before moving into more demanding examination-level work.
            </Text>
          </Box>


          <Box
            bg={platformBg}
            borderRadius="xl"
            p={5}
          >
            <Text fontSize="2xl" mb={2}>
              🎯
            </Text>

            <Heading size="sm" mb={2}>
              Practice
            </Heading>

            <Text
              fontSize={{ base: "md", md: "md" }}
              color={mutedText}
              lineHeight="1.7"
            >
              Apply concepts through structured practice, with feedback,
              doubt-clearing and guidance on improving speed and accuracy.
            </Text>
          </Box>


          <Box
            bg={platformBg}
            borderRadius="xl"
            p={5}
          >
            <Text fontSize="2xl" mb={2}>
              📈
            </Text>

            <Heading size="sm" mb={2}>
              Assess & Improve
            </Heading>

            <Text
              fontSize={{ base: "md", md: "md" }}
              color={mutedText}
              lineHeight="1.7"
            >
              Use scheduled assessments, examinations and practice results to
              identify gaps and progressively build examination readiness.
            </Text>
          </Box>

        </SimpleGrid>

      </Box>

    </Box>





      {/* ================= PREPARATION AREAS ================= */}

      <Box
        py={6}
        px={6}
        id="preparation-areas"
      >
        <VStack
          mb={10}
          spacing={3}
        >
          <Heading
            size="xl"
            textAlign="center"
          >
            Our Guided Preparation Areas
          </Heading>

          <Text
            textAlign="center"
            color={mutedText}
          >
            Choose the examination pathway relevant to your preparation
          </Text>
        </VStack>

        <VStack
          spacing={16}
          align="stretch"
          maxW="1600px"
          mx="auto"
        >

          {categories.map((category) => {
            const isLight = theme === "light";
            const baseColor = category.accent;

            return (
              <Box
                key={category.slug}
                id={`category-${category.slug}`}
              >

                {/* ================= CATEGORY HEADING ================= */}

                <Heading
                  size="lg"
                  mb={2}
                  borderLeft="4px solid"
                  borderColor={`${baseColor}.400`}
                  pl={3}
                >
                  {category.name}
                </Heading>

                <Text
                  color={mutedText}
                  mb={6}
                  pl={4}
                  maxW="800px"
                >
                  {category.description}
                </Text>

                {/* ================= CATEGORY CONTENT BOX ================= */}

                <Box
                  bg={
                    isLight
                      ? `${baseColor}.50`
                      : `${baseColor}.900`
                  }
                  rounded="2xl"
                  overflow="hidden"
                  shadow="xl"
                >

                  {/* Category colour strip */}

                  <Box
                    height="8px"
                    bg={`${baseColor}.500`}
                  />

                  <Box
                    p={{ base: 6, md: 10 }}
                  >

                    {/* ================= CATEGORY LONG DESCRIPTION ================= */}

                    {category.longDescription && (
                      <Box
                        mb={10}
                        p={{ base: 2, md: 4 }}
                        color={
                          isLight
                            ? "gray.700"
                            : "gray.200"
                        }
                      >
                        <B2CMarkdownLongText
                          content={category.longDescription}
                          textAlign="left"
                        />
                      </Box>
                    )}

                    {/* ================= EXAMINATION OPTIONS ================= */}

                    <SimpleGrid
                      columns={{
                        base: 1,
                        md: 2,
                        lg: category.domains.length > 2 ? 4 : 2,
                      }}
                      spacing={8}
                    >

                      {category.domains.map((domain) => {
                        const dColor = domain.accent;

                        return (
                          <Box
                            key={domain.slug}
                            bg={
                              isLight
                                ? "white"
                                : "gray.800"
                            }
                            border="1px solid"
                            borderColor={`${dColor}.200`}
                            rounded="xl"
                            shadow="md"
                            cursor="pointer"
                            overflow="hidden"
                            display="flex"
                            flexDirection="column"
                            transition="all 0.3s ease"
                            _hover={{
                              transform: "translateY(-6px)",
                              shadow: "xl",
                            }}
                            onClick={() =>
                              navigate(
                                `/coaching/domain/${domain.slug}`
                              )
                            }
                          >

                            {/* ================= CARD HEADLINE ================= */}

                            <Box
                              position="relative"
                              height="82px"
                            >

                              <Box
                                position="absolute"
                                top="30px"
                                left={0}
                                right={0}
                                height="52px"
                                bg={
                                  isLight
                                    ? `${dColor}.300`
                                    : `${dColor}.600`
                                }
                              />

                              <Box
                                position="absolute"
                                top="30px"
                                left={0}
                                right={0}
                                height="52px"
                                display="flex"
                                alignItems="center"
                                px={5}
                                zIndex={1}
                              >

                                <VStack
                                  align="start"
                                  spacing={0}
                                >

                                  <Heading
                                    size="sm"
                                    color={
                                      isLight
                                        ? "gray.800"
                                        : "white"
                                    }
                                    fontWeight="semibold"
                                    noOfLines={1}
                                  >
                                    {domain.name}
                                  </Heading>

                                  <Text
                                    fontSize="xs"
                                    color={
                                      isLight
                                        ? "gray.700"
                                        : "gray.200"
                                    }
                                    fontWeight="medium"
                                  >
                                    {domain.subtitle}
                                  </Text>

                                </VStack>

                              </Box>

                            </Box>

                            {/* ================= CARD CONTENT ================= */}

                            <Box
                              p={5}
                              flex="1"
                              display="flex"
                              flexDirection="column"
                            >

                              <Box
                                flex="1"
                                fontSize="sm"
                                color={
                                  isLight
                                    ? "gray.700"
                                    : "gray.200"
                                }
                              >

                                {domain.description && (
                                  <ReactMarkdown
                                    remarkPlugins={[
                                      remarkGfm,
                                    ]}
                                    components={
                                      markdownComponents
                                    }
                                  >
                                    {domain.description}
                                  </ReactMarkdown>
                                )}

                              </Box>

                              {/* ================= CTA ================= */}

                              <Box mt="auto">

                                <Button
                                  mt={6}
                                  width="full"
                                  size="sm"
                                  borderRadius="lg"
                                  fontWeight="semibold"
                                  bg={`${dColor}.500`}
                                  color="white"
                                  _hover={{
                                    bg: `${dColor}.600`,
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();

                                    navigate(
                                      `/coaching/domain/${domain.slug}`
                                    );
                                  }}
                                >
                                  Learn More
                                </Button>

                              </Box>

                            </Box>

                          </Box>
                        );
                      })}

                    </SimpleGrid>

                  </Box>

                </Box>

              </Box>
            );
          })}

        </VStack>
      </Box>


{/* ================= HOW GUIDED PREPARATION WORKS ================= */}

<Box
  maxW="1600px"
  mx="auto"
  px={{ base: 6, md: 8 }}
  py={{ base: 10, md: 14 }}
>
  <VStack spacing={3} mb={10}>
    <Heading
      size={{ base: "lg", md: "xl" }}
      textAlign="center"
      color={sectionHeadingColor}
    >
      How Guided Preparation Works
    </Heading>

    <Text
      textAlign="center"
      color={mutedText}
      fontSize={{ base: "md", md: "lg" }}
      maxW="800px"
      lineHeight="1.8"
    >
      Guided preparation combines scheduled online classes, structured
      practice and regular assessment so that preparation progresses
      systematically rather than relying on practice alone.
    </Text>
  </VStack>

  <SimpleGrid
    columns={{ base: 1, md: 2 }}
    spacing={6}
  >

    {/* STEP 1 */}

    <Box
      bg={cardBg}
      border="1px solid"
      borderColor={lightBorder}
      borderRadius="xl"
      shadow="sm"
      p={{ base: 6, md: 7 }}
    >
      <HStack align="start" spacing={4}>

        <Box
          minW="46px"
          h="46px"
          borderRadius="full"
          bg={platformBg}
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <Text
            fontSize="lg"
            fontWeight="bold"
            color={stepNumberColor}
          >
            1
          </Text>
        </Box>

        <Box>
          <Heading size="sm" mb={3}>
            Build the Foundation
          </Heading>

          <Text
            fontSize={{ base: "md", md: "md" }}
            color={mutedText}
            lineHeight="1.75"
          >
            Scheduled online classes begin by ensuring that the underlying
            concepts are properly understood. Where a student has foundation
            gaps, these are addressed before progressing to more advanced
            examination-level questions.
          </Text>
        </Box>

      </HStack>
    </Box>


    {/* STEP 2 */}

    <Box
      bg={cardBg}
      border="1px solid"
      borderColor={lightBorder}
      borderRadius="xl"
      shadow="sm"
      p={{ base: 6, md: 7 }}
    >
      <HStack align="start" spacing={4}>

        <Box
          minW="46px"
          h="46px"
          borderRadius="full"
          bg={platformBg}
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <Text
            fontSize="lg"
            fontWeight="bold"
            color={stepNumberColor}
          >
            2
          </Text>
        </Box>

        <Box>
          <Heading size="sm" mb={3}>
            Clarify, Apply & Improve
          </Heading>

          <Text
            fontSize={{ base: "md", md: "md" }}
            color={mutedText}
            lineHeight="1.75"
          >
            Classes provide opportunities for additional doubt-clearing,
            problem-solving guidance and practical techniques for improving
            speed and accuracy as students become more comfortable with the
            examination style.
          </Text>
        </Box>

      </HStack>
    </Box>


    {/* STEP 3 */}

    <Box
      bg={cardBg}
      border="1px solid"
      borderColor={lightBorder}
      borderRadius="xl"
      shadow="sm"
      p={{ base: 6, md: 7 }}
    >
      <HStack align="start" spacing={4}>

        <Box
          minW="46px"
          h="46px"
          borderRadius="full"
          bg={platformBg}
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <Text
            fontSize="lg"
            fontWeight="bold"
            color={stepNumberColor}
          >
            3
          </Text>
        </Box>

        <Box>
          <Heading size="sm" mb={3}>
            Assess Through the Learning Platform
          </Heading>

          <Text
            fontSize={{ base: "md", md: "md" }}
            color={mutedText}
            lineHeight="1.75"
          >
            The Eduvic Learning Platform supports scheduled assessments and
            examinations specifically for guided-preparation students.
            Assessments can be scheduled around the student's study calendar
            to encourage a testing-based approach to preparation.
          </Text>
        </Box>

      </HStack>
    </Box>


    {/* STEP 4 */}

    <Box
      bg={cardBg}
      border="1px solid"
      borderColor={lightBorder}
      borderRadius="xl"
      shadow="sm"
      p={{ base: 6, md: 7 }}
    >
      <HStack align="start" spacing={4}>

        <Box
          minW="46px"
          h="46px"
          borderRadius="full"
          bg={platformBg}
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <Text
            fontSize="lg"
            fontWeight="bold"
            color={stepNumberColor}
          >
            4
          </Text>
        </Box>

        <Box>
          <Heading size="sm" mb={3}>
            Practice, Review & Consolidate
          </Heading>

          <Text
            fontSize={{ base: "md", md: "md" }}
            color={mutedText}
            lineHeight="1.75"
          >
            Relevant Eduvic Practice Platform resources are typically
            provided as part of guided preparation. Students use these
            resources extensively to build speed, practice concepts and
            generate performance feedback between scheduled classes.
          </Text>
        </Box>

      </HStack>
    </Box>

  </SimpleGrid>


  {/* DIGITAL LESSONS */}

  <Box
    mt={8}
    bg={platformBg}
    borderRadius="xl"
    p={{ base: 6, md: 8 }}
  >
    <HStack
      align="start"
      spacing={4}
    >

      <Text fontSize="2xl">
        📚
      </Text>

      <Box>
        <Heading size="sm" mb={3}>
          Continue Learning Between Classes
        </Heading>

        <Text
          fontSize={{ base: "md", md: "md" }}
          color={mutedText}
          lineHeight="1.75"
        >
          Key teaching from scheduled classes is consolidated into
          digitised online lessons and supporting material. These are shared
          with the student after class and remain accessible through the
          Eduvic Learning Platform for revision at any time.
        </Text>
      </Box>

    </HStack>
  </Box>

</Box>

{/* ================= POWERED BY EDUVIC LEARNING PLATFORM ================= */}

{/* ================= POWERED BY EDUVIC LEARNING PLATFORM ================= */}

<Box
  maxW="1600px"
  mx="auto"
  px={{ base: 6, md: 8 }}
  py={{ base: 10, md: 14 }}
>

  {/* SECTION INTRO */}

{/* SECTION INTRO */}

<VStack
  spacing={4}
  mb={8}
  textAlign="center"
>
  <Text
    fontSize="sm"
    fontWeight="bold"
    color={accentText}
    textTransform="uppercase"
    letterSpacing="wide"
  >
    THE EDUVIC LEARNING PLATFORM
  </Text>

  <Heading
    size={{ base: "lg", md: "xl" }}
  >
    The Learning Environment Behind Eduvic Coaching
  </Heading>

  <Text
    color={mutedText}
    fontSize={{ base: "md", md: "lg" }}
    lineHeight="1.8"
    maxW="850px"
  >
    Eduvic Coaching is delivered through the Eduvic Learning Platform,
    which provides Guided Preparation students with a dedicated online
    environment for classes, lessons, digital content, assessments and
    examinations throughout their preparation.
  </Text>
</VStack>


  {/* PLATFORM CAPABILITIES */}

  <Box
    bg={platformBg}
    borderRadius="2xl"
    p={{ base: 6, md: 10 }}
  >

    <SimpleGrid
      columns={{ base: 1, md: 3 }}
      spacing={6}
    >

      {/* LEARNING */}

      <Box
        bg={platformCardBg}
        borderRadius="xl"
        p={{ base: 6, md: 7 }}
        border="1px solid"
        borderColor={lightBorder}
      >

        <Text fontSize="2xl" mb={3}>
          📚
        </Text>

        <Heading size="sm" mb={4}>
          Learning
        </Heading>

        <VStack
          align="start"
          spacing={3}
        >

          <Text fontSize="md" color={mutedText}>
            • Scheduled online classes
          </Text>

          <Text fontSize="md" color={mutedText}>
            • Digitised lessons and class material
          </Text>

          <Text fontSize="md" color={mutedText}>
            • Supporting digital learning content
          </Text>

          <Text fontSize="md" color={mutedText}>
            • Lessons available after class for revision
          </Text>

        </VStack>

      </Box>


      {/* ASSESSMENT */}

      <Box
        bg={platformCardBg}
        borderRadius="xl"
        p={{ base: 6, md: 7 }}
        border="1px solid"
        borderColor={lightBorder}
      >

        <Text fontSize="2xl" mb={3}>
          📝
        </Text>

        <Heading size="sm" mb={4}>
          Assessment
        </Heading>

        <VStack
          align="start"
          spacing={3}
        >

          <Text fontSize="md" color={mutedText}>
            • Scheduled Guided Preparation assessments
          </Text>

          <Text fontSize="md" color={mutedText}>
            • Periodic examinations
          </Text>

          <Text fontSize="md" color={mutedText}>
            • Assessment and examination feedback
          </Text>

          <Text fontSize="md" color={mutedText}>
            • Testing aligned with the student's preparation calendar
          </Text>

        </VStack>

      </Box>


      {/* PROGRESS */}

      <Box
        bg={platformCardBg}
        borderRadius="xl"
        p={{ base: 6, md: 7 }}
        border="1px solid"
        borderColor={lightBorder}
      >

        <Text fontSize="2xl" mb={3}>
          📊
        </Text>

        <Heading size="sm" mb={4}>
          Progress
        </Heading>

        <VStack
          align="start"
          spacing={3}
        >

          <Text fontSize="md" color={mutedText}>
            • Assessment results
          </Text>

          <Text fontSize="md" color={mutedText}>
            • Performance information
          </Text>

          <Text fontSize="md" color={mutedText}>
            • Student progress tracking
          </Text>

          <Text fontSize="md" color={mutedText}>
            • Ongoing access to preparation activity
          </Text>

        </VStack>

      </Box>

    </SimpleGrid>


    {/* GUIDED PREPARATION + PRACTICE PLATFORM */}

    <Box
      mt={8}
      bg={platformCardBg}
      borderRadius="xl"
      p={{ base: 6, md: 8 }}
      border="1px solid"
      borderColor={lightBorder}
    >

      <HStack
        align="start"
        spacing={4}
      >

        <Text
          fontSize="2xl"
          flexShrink={0}
        >
          🔗
        </Text>

        <Box>

          <Heading
            size="sm"
            mb={3}
          >
            Guided Preparation + Independent Practice
          </Heading>

          <Text
            fontSize={{ base: "md", md: "md" }}
            color={mutedText}
            lineHeight="1.75"
          >
            For Guided Preparation students, the Eduvic Learning Platform
            is the environment used to deliver online classes, lessons,
            digital content, scheduled assessments and examinations.
            Where relevant to the selected preparation pathway, Eduvic
            also provides complimentary access to the corresponding
            Eduvic Practice Platform package so students can continue
            independent practice and build speed, accuracy and familiarity
            between guided sessions.
          </Text>

        </Box>

      </HStack>

    </Box>


  </Box>

</Box>

{/* ================= COACHING + PRACTICE ================= */}

<Box
  maxW="1600px"
  mx="auto"
  px={{ base: 6, md: 8 }}
  py={{ base: 10, md: 14 }}
>
  <VStack
    spacing={4}
    mb={8}
    textAlign="center"
  >
    <Heading
      size={{ base: "lg", md: "xl" }}
    >
      Guided Preparation, Supported by Independent Practice
    </Heading>

    <Text
      color={mutedText}
      fontSize={{ base: "md", md: "lg" }}
      lineHeight="1.8"
      maxW="850px"
    >
      Eduvic Coaching provides the structured teaching and guidance.
      Relevant Eduvic Practice Platform access is provided alongside the
      coaching so students can continue practising independently between
      guided sessions.
    </Text>
  </VStack>


  <Flex
    direction={{ base: "column", md: "row" }}
    align="stretch"
    justify="center"
    gap={{ base: 4, md: 3 }}
  >

    {/* EDUVIC COACHING */}

    <Box
      flex="1"
      maxW={{ base: "100%", md: "650px" }}
      bg={cardBg}
      border="2px solid"
      borderColor="purple.200"
      borderRadius="2xl"
      p={{ base: 6, md: 8 }}
      shadow="md"
    >
      <HStack
        spacing={3}
        mb={2}
      >
        <Text fontSize="2xl">
          👨‍🏫
        </Text>

        <Heading
          size={{ base: "md", md: "lg" }}
        >
          Eduvic Coaching
        </Heading>
      </HStack>

      <Text
        fontSize={{ base: "md", md: "lg" }}
        color={mutedText}
        mb={5}
      >
        Structured Guided Preparation
      </Text>

      <VStack
        align="start"
        spacing={3}
      >
        {[
          "Scheduled online coaching classes",
          "Foundation concept building and clarification",
          "Additional doubt-clearing and targeted guidance",
          "Tips and strategies for improving speed and accuracy",
          "Scheduled assessments and examinations for Guided Preparation students",
          "Lessons, exams and digital learning content delivered through the Eduvic Learning Platform",
          "Progressive preparation aligned with the student's study calendar",
        ].map((item) => (
          <HStack
            key={item}
            spacing={3}
            align="start"
          >
            <Icon
              as={FaCheckCircle}
              color="purple.400"
              boxSize={4}
              mt={1}
              flexShrink={0}
            />

            <Text
              fontSize={{ base: "md", md: "md" }}
              lineHeight="1.6"
            >
              {item}
            </Text>
          </HStack>
        ))}
      </VStack>
    </Box>


    {/* PLUS */}

    <Flex
      align="center"
      justify="center"
      px={{ base: 0, md: 2 }}
      py={{ base: 1, md: 0 }}
    >
      <Box
        w="42px"
        h="42px"
        borderRadius="full"
        bg={secondaryHeadingColor}
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Text
          fontSize="2xl"
          fontWeight="bold"
          color={mutedText}
        >
          +
        </Text>
      </Box>
    </Flex>


    {/* EDUVIC PRACTICE PLATFORM */}

    <Box
      flex="1"
      maxW={{ base: "100%", md: "650px" }}
      bg={cardBg}
      border="2px solid"
      borderColor="blue.200"
      borderRadius="2xl"
      p={{ base: 6, md: 8 }}
      shadow="md"
    >
      <HStack
        spacing={3}
        mb={2}
      >
        <Text fontSize="2xl">
          📚
        </Text>

        <Heading
          size={{ base: "md", md: "lg" }}
        >
          Eduvic Practice Platform
        </Heading>
      </HStack>

      <Text
        fontSize={{ base: "md", md: "lg" }}
        color={mutedText}
        mb={5}
      >
        Complimentary Independent Practice
      </Text>

      <VStack
        align="start"
        spacing={3}
      >
        {[
          "Relevant Practice Platform package provided where applicable",
          "Subject-wise examination-style question sets",
          "Timed and Untimed practice modes",
          "Unlimited repeatable practice attempts",
          "Performance and question-level analysis",
          "Explanations to identify and strengthen improvement areas",
          "Independent practice between scheduled coaching sessions",
        ].map((item) => (
          <HStack
            key={item}
            spacing={3}
            align="start"
          >
            <Icon
              as={FaCheckCircle}
              color="blue.400"
              boxSize={4}
              mt={1}
              flexShrink={0}
            />

            <Text
              fontSize={{ base: "md", md: "md" }}
              lineHeight="1.6"
            >
              {item}
            </Text>
          </HStack>
        ))}
      </VStack>
    </Box>

  </Flex>
</Box>

      {/* ================= CTA ================= */}
      <Box maxW="1600px" mx="auto" my={10} textAlign="center" py={8}>
        <Heading size="md" mb={4}>Interested in Guided Preparation?</Heading>
        <Button
          colorScheme="blue"
          size="lg"
          onClick={() => navigate(`/coaching/contact?topic=free-trial`)}
        >
          Book Your Free Trial Class
        </Button>
      </Box>

    </PublicLayout>
  );
}

export default ServicesPage;